import React, { useState, useEffect } from 'react';
import { Card } from 'react-bootstrap';
import { TestTube } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useFetchLatestCategoryTestQuery } from '#store/apis/laboratoryTestsApi';
import moment from 'moment';

const CATEGORY_NAMES = {
  C55: {
    pl: 'C55 Morfologia krwi',
    en: 'C55 Complete Blood Count',
  },
  M71: {
    pl: 'M71 Lipidogram',
    en: 'M71 Lipidogram',
  },
  L43: {
    pl: 'L43 Glukoza we krwi',
    en: 'L43 Blood Glucose',
  },
  A01: {
    pl: 'A01 Badanie ogólne moczu',
    en: 'A01 Urinalysis',
  },
  M37: {
    pl: 'M37 Kreatynina',
    en: 'M37 Creatinine',
  },
};

const getStatusColor = (daysSinceLastTest) => {
  if (daysSinceLastTest === null) return 'secondary';
  if (daysSinceLastTest >= 365) return 'danger';
  if (daysSinceLastTest >= 180) return 'warning';
  return 'success';
};

const getStatusText = (daysSinceLastTest) => {
  if (daysSinceLastTest === null) return 'No Data';
  if (daysSinceLastTest >= 365) return 'Overdue';
  if (daysSinceLastTest >= 180) return 'Due Soon';
  return 'Up to Date';
};

const statusKeyFromText = (statusText) => statusText.toLowerCase().replace(/\s+/g, '_');

/**
 * Odpowiednik v0 `lab-status-card.tsx` — Bootstrap + pasek postępu i data w jednym rzędzie.
 */
export function LabStatusCard() {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const c55Data = useFetchLatestCategoryTestQuery(
    { icd9_code: 'C55' },
    { skip: false, refetchOnMountOrArgChange: true },
  );
  const m71Data = useFetchLatestCategoryTestQuery(
    { icd9_code: 'M71' },
    { skip: false, refetchOnMountOrArgChange: true },
  );
  const L43Data = useFetchLatestCategoryTestQuery(
    { icd9_code: 'L43' },
    { skip: false, refetchOnMountOrArgChange: true },
  );
  const a01Data = useFetchLatestCategoryTestQuery(
    { icd9_code: 'A01' },
    { skip: false, refetchOnMountOrArgChange: true },
  );
  const m37Data = useFetchLatestCategoryTestQuery(
    { icd9_code: 'M37' },
    { skip: false, refetchOnMountOrArgChange: true },
  );

  const categoryData = [
    { code: 'C55', data: c55Data },
    { code: 'M71', data: m71Data },
    { code: 'L43', data: L43Data },
    { code: 'A01', data: a01Data },
    { code: 'M37', data: m37Data },
  ];

  const isLoading = categoryData.some(({ data }) => {
    if (data.error && data.error.status === 404) {
      return false;
    }
    return data.isLoading || data.isFetching;
  });

  const [showContent, setShowContent] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 10000);
    return () => clearTimeout(timer);
  }, []);

  const getCategoryName = (code) => {
    const category = CATEGORY_NAMES[code];
    if (category) {
      const name = currentLanguage === 'pl' ? category.pl : category.en;
      if (name && name.length > 50) {
        return `${name.substring(0, 47)}...`;
      }
      return name;
    }
    const testData = categoryData.find((cat) => cat.code === code)?.data?.data;
    if (testData?.category_name_pl || testData?.category_name_en) {
      const name = currentLanguage === 'pl' ? testData.category_name_pl : testData.category_name_en;
      if (name && name.length > 50) {
        return `${name.substring(0, 47)}...`;
      }
      return name;
    }
    return `${code} ${t('dashboard.category-test')}`;
  };

  const badgeClassFor = (statusColor) => {
    if (statusColor === 'success') return 'lab-status-badge lab-status-badge--valid';
    if (statusColor === 'danger') return 'lab-status-badge lab-status-badge--overdue';
    if (statusColor === 'warning') return 'lab-status-badge lab-status-badge--soon';
    return 'lab-status-badge lab-status-badge--nodata';
  };

  const barClassFor = (statusColor) => {
    if (statusColor === 'success')
      return 'lab-status-progress-fill lab-status-progress-fill--valid';
    if (statusColor === 'danger')
      return 'lab-status-progress-fill lab-status-progress-fill--overdue';
    if (statusColor === 'warning') return 'lab-status-progress-fill lab-status-progress-fill--soon';
    return 'lab-status-progress-fill lab-status-progress-fill--muted';
  };

  const dateFormat = currentLanguage === 'pl' ? 'DD.MM.YYYY' : 'MMM D, YYYY';

  return (
    <Card className='lab-status-bootstrap-card border-0 shadow h-100 d-flex flex-column'>
      <Card.Header className='bg-white border-bottom py-3 pb-2'>
        <div
          className='mb-0 d-flex align-items-center gap-2 fw-semibold text-dark'
          style={{ fontSize: '1rem' }}
        >
          <TestTube
            className='flex-shrink-0'
            style={{ width: '1.25rem', height: '1.25rem', color: 'var(--primary)' }}
            strokeWidth={2}
            aria-hidden
          />
          {t('dashboard.lab-status')}
        </div>
      </Card.Header>
      <Card.Body className='pt-3 d-flex flex-column flex-grow-1'>
        {isLoading && !showContent ? (
          <div className='text-center py-4 flex-grow-1 d-flex align-items-center justify-content-center'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
          </div>
        ) : (
          <div className='d-flex flex-column gap-4'>
            {categoryData.map(({ code, data }) => {
              const testData = data.data;
              const daysSinceLastTest = testData?.days_since_last_test;
              const statusColor = getStatusColor(daysSinceLastTest);
              const statusText = getStatusText(daysSinceLastTest);
              const categoryName = getCategoryName(code);
              const progressPct = testData
                ? Math.min(100, ((daysSinceLastTest ?? 0) / 365) * 100)
                : 0;

              return (
                <div key={code} className='lab-status-row'>
                  <div className='d-flex align-items-center justify-content-between gap-2 mb-2'>
                    <span className='lab-status-test-name text-truncate'>{categoryName}</span>
                    {testData ? (
                      <span className={`${badgeClassFor(statusColor)} flex-shrink-0`}>
                        {t(`dashboard.status.${statusKeyFromText(statusText)}`)}
                      </span>
                    ) : (
                      <span className='lab-status-badge lab-status-badge--nodata flex-shrink-0'>
                        {t('dashboard.status.no_data')}
                      </span>
                    )}
                  </div>
                  {testData ? (
                    <div className='d-flex align-items-center gap-3'>
                      <div className='lab-status-progress-track flex-grow-1 min-w-0'>
                        <div
                          className={barClassFor(statusColor)}
                          style={{ width: `${progressPct}%` }}
                        />
                      </div>
                      <span className='lab-status-date flex-shrink-0'>
                        {moment(testData.latest_date).format(dateFormat)}
                      </span>
                    </div>
                  ) : (
                    <p className='text-muted small mb-0'>{t('dashboard.no-data-available')}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
